/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// import { log } from 'utils/logger';
import { settings } from 'config/config';
import jss from 'jss';
import preset from 'jss-preset-default' // a set of default plugins to JSS automatically defined (http://cssinjs.org/jss-preset-default?v=v4.0.1) to make DX better
jss.setup(preset());



/* --------------- UTILITIES : CSS --------------- */
// shared across all components

// JSS ... used to create style tag in <head /> element (http://cssinjs.org/?v=v9.4.0)
// passing styles from component we're using them in, options are JSS options defaulted to empty object
let createStyles = (styles, options = {}) => jss.createStyleSheet(styles, options).attach();

// baseline font size that all other element sizes will be based off of
// used in index_styles for body font-size and used for pxToEm conversion
const fontSizeBaseline = 16;

// converts pixels in styles to EMs
// this allows us to create styles as pixels to better match given designs, then convert those to relative units so we can achieve responsive design
let pxToEm = (val) => parseFloat(val) / fontSizeBaseline + "em";

let pxToEmWithBaseline = ({ value, fontSizeBaseline }) => parseFloat(value) / fontSizeBaseline + "em";

// create some values as rems so they aren't as affected by font size
let pxToRem = (val) => parseFloat(val) / fontSizeBaseline + "rem";

// convert em to pixel (helpful with perctage conversions)
let emToPx = (val) => parseFloat(val) * fontSizeBaseline + "px";

// convert percentages to pixels then to em value (needed for dynamic content sizing based off of content space DOM insertions)
let percToEm = (parSize, elSize) => pxToEm(parseFloat(emToPx(parSize))*(parseFloat(elSize)/100)); //log(parSize, elSize);

// convert style string px to em (used by store.js to convert settings file  styles from pixels to ems)
let replacePXinStringWithEM = (passedStyle) => {
	// save version of passed style
	let style = passedStyle;
	// if style contains pixels
	if (style.match(/(\d*(\.\d*)?)px/g)) {
		// store individual px in array
		let pxVals = style.match(/(\d*(\.\d*)?)px/g);
		// log(style);
		// log(pxVals);
		// convert to ems
		let emVals = pxVals.map(px => pxToEm(px));
		// log(emVals);
		// for every pixel we found, replace the px in the original style with the converted em (by matching the index of that array)
		pxVals.forEach((val, i) => {
			style = style.replace(val, emVals[i]);
		})
		// log(style);
	}
	return style;
};

// styles (as object) are passed in here and if any of its rules have px values they will be converted to em
let convertStylesPXtoEM = (passedStyles) => {
	// save version of passed styles
	let styles = Object.assign({}, passedStyles);
	// get each style
    Object.entries(styles).forEach((style) => {
        // check if style contains child styles (probably need to make this smarter as this only goes one level deep)
        if (typeof style[1] === "object") {
            Object.entries(style[1]).forEach((nestedStyle) => {
            	// log(nestedStyle[0]);
            	styles[style[0]][nestedStyle[0]] = replacePXinStringWithEM(nestedStyle[1]);
            });
        } else {
            styles[style[0]] = replacePXinStringWithEM(style[1]);
        }
    });
    // log(styles);
    return styles;
};

// animations
const animations = {
	clicked: {
		animationName: 'rubberBand',
	    animationDelay: '0s',
	    animationDuration: '.5s',
	},
	imageHover: {
		animationName: 'pulse',
        animationDelay: '0s',
        animationDuration: '.5s'
	},
	textElements: {
		// used to animate in textual elements in screens mostly
		'& .title, .logo.insticator, .giftcard, .bodycopy, .ctas': {
			opacity: '0'
		},
		'& .title, .logo.insticator, .giftcard': {
			animationName: 'fadeInDown',
			animationDuration: '.3s',
			animationDelay: '0s',
            animationFillMode: 'forwards'
        },
		'& .bodycopy': {
			animationName: 'fadeInDown',
			animationDuration: '.3s',
			animationDelay: '0s',
            animationFillMode: 'forwards'
        },
        '& .ctas': {
			animationName: 'fadeInUp',
			animationDuration: '.3s',
			animationDelay: '0s',
            animationFillMode: 'forwards'
        }
	}
};

// heights
const heights = {
	header: pxToEm(40), // 45
	// footer: settings.embed.rewards.enabled ? pxToEm(25) : pxToEm(35), // 40
	// rewardsindicator: settings.embed.rewards.enabled ? pxToEm(70) : '0em' // 74 // only set a height if rewards are enabled
	// body height based on (embedHeight - (header + footer)) = 350 - 159 = 191
	// 350px total height available
	// modifying the above heights will automatically modify the body height (handled in body.js)
};

// spacing
const spacing = {
	sides: pxToEm(20), // 15
	left: pxToEm(15),
	right: pxToEm(15)
};

// convert hex to rgb (with opacity param adjuster)
const convertHexToRGB = (hex,opacity) => {
    hex = hex.replace('#','');
    let r = parseInt(hex.substring(0,2), 16);
    let g = parseInt(hex.substring(2,4), 16);
    let b = parseInt(hex.substring(4,6), 16);
    let result = 'rgba('+r+','+g+','+b+','+opacity/100+')';
    return result;
};

// colors
const color = {
	black: '#000000',
	white: "#ffffff",
	greyLight: "#eeeeee",
	greyMedium: "#D8D8D8",
	greyDark: "#808080",
	greyDarker: "#4a4a4a",
	green: "#4EA808", // rgb(78,168,8)
	red: "#E22121", // rgb(226,33,33)
	actionRed: "#E42C2C",
	pink: "#F01C63",
	blueDark: "#042049",
	blueLight: "#1494f4",
	orangeLight: "#e8aa0c",
	// themes
	rewards: '#e8aa0c',
	brand: settings.embed.brandColor === null || settings.embed.brandColor === undefined ? '#e8aa0c' : settings.embed.brandColor, // comes from user selection from dashboard (used for progressbar, bullets, highlights, etc)
};



// .......... BOX SHADOW .......... //
// shadow styles
const boxShadow = {
	blue: { boxShadow: replacePXinStringWithEM(`0 0 20px 0 ${convertHexToRGB(color.blueLight, 90)}`) },
	blueLarge: { boxShadow: replacePXinStringWithEM(`0 0 40px 0 ${convertHexToRGB(color.blueLight, 90)}`) },
	grey: { boxShadow: replacePXinStringWithEM(`0 0 10px 0 rgba(0,0,0,0.15)`) },
	greyLarge: { boxShadow: replacePXinStringWithEM(`0 0 20px 0 rgba(0,0,0,0.15)`) },
	greySmall: { boxShadow: replacePXinStringWithEM(`0 0 2px 0 rgba(0,0,0,0.02)`) }
};



// .......... TEXT SHADOW .......... //
const textShadow = {
	medium: { textShadow: replacePXinStringWithEM(`1px 1px 3px ${color.greyDark}`) },
	dark: { textShadow: replacePXinStringWithEM(`1px 1px 5px ${color.black}`) },
	spread: { textShadow: replacePXinStringWithEM(`0px 0px 10px ${color.black}`) }
};



// .......... TEXT SHADOW .......... //
const backgrounds = {
	standout: `linear-gradient(to bottom, #0c418d 0%,#042049 100%)`,
	gray: `linear-gradient(to bottom, #4a4a4a 0%,#000000 100%)`
};


// center element absolutely
const centerAbsolutely = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)'
};

// full width and height
const fullSize = {
	width: '100%',
	height: '100%'
};

// background image
const cssBGImage = {
	backgroundColor: 'transparent',
	backgroundPosition: '50%',
	backgroundSize: 'contain',
	backgroundRepeat: 'no-repeat'
	// backgroundImage will be placed per unique style
};
// giftcard image
const cssPRImage = {
    backgroundColor: 'transparent',
    backgroundPosition: '50%',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat'
    // backgroundImage will be placed per unique style
};
// general image settings
const cssImage = {
	backgroundColor: 'transparent',
	backgroundSize: 'contain',
	backgroundRepeat: 'no-repeat',
	backgroundPosition: '50%',
	display: 'inline-block',
	verticalAlign: 'middle',
	'&:hover': {
		transition: 'all .2s linear 0s'
	}
};

// image types
const imageTypes = {
	icon: {
		extend: cssImage
	},
	logo: {
		extend: cssImage
	},
	photo: {
		extend: cssBGImage
	},
    card: {
        extend: cssPRImage
    }
};

// button styles
const button = {
    skip: {

    }
};


// points total display text (used in MR, questionnaire)
const pointsTotalDisplayText = {
	fontWeight: '600',
	marginTop: pxToEm(-24)
};


// embed styles
const embedStyles = settings.embed.styles;
const embedCorners = { borderRadius: pxToEm(10) };

// display types
const displayTypes = {
	inline: {
		display: 'inline-block',
		verticalAlign: 'top'
	},
	overlay: {
		// height: '35%',  // this will probably come from settings
		// width: '35%',  // this will probably come from settings
		position: 'absolute',
		zIndex: '4',
		'&.replace': {
			zIndex: '2',
			extend: centerAbsolutely // uses JSS extend functionality (http://cssinjs.org/jss-extend)
		},
		'&.modal': {
			// background: 'rgba(0,0,0,.5)', // this will probably come from settings
			zIndex: '3',
			extend: centerAbsolutely,
			'& $inner': {
				// background: '#fff', // this will probably come from settings
				// height: '70%', // this will probably come from settings
				// width: '70%', // this will probably come from settings
				extend: centerAbsolutely
			}
		},
		'&.outside': {
			'&-top': {
				'&-left': 	{ transform: 'translate(0%, -100%)', top: '0%', left: '0%' },
				'&-center': { transform: 'translate(-50%, -100%)', top: '0%', left: '50%' },
				'&-right': 	{ transform: 'translate(0%, -100%)', top: '0%', right: '0%' }
			},
			'&-right': {
				'&-top': 	{ transform: 'translate(100%, 0%)', top: '0%', right: '0%' },
				'&-center': { transform: 'translate(100%, -50%)', top: '50%', right: '0%' },
				'&-bottom': { transform: 'translate(100%, 0%)', bottom: '0%', right: '0%' }
			},
			'&-bottom': {
				'&-left': 	{ transform: 'translate(0%, 100%)', bottom: '0%', left: '0%' },
				'&-center': { transform: 'translate(-50%, 100%)', bottom: '0%', left: '50%' },
				'&-right': 	{ transform: 'translate(0%, 100%)', bottom: '0%', right: '0%' }
			},
			'&-left': {
				'&-top': 	{ transform: 'translate(-100%, 0%)', top: '0%', left: '0%' },
				'&-center': { transform: 'translate(-100%, -50%)', top: '50%', left: '0%' },
				'&-bottom': { transform: 'translate(-100%, 0%)', bottom: '0%', left: '0%' }
			}
		},
		'&.inside': {
			'&-top': {
				'&-left': 	{ top: '0%', left: '0%' },
				'&-center': { transform: 'translateX(-50%)', top: '0%', left: '50%' },
				'&-right': 	{ top: '0%', right: '0%' }
			},
			'&-center': {
				'&-left': 	{ transform: 'translateY(-50%)', top: '50%', left: '0%' },
				'&-center': { transform: 'translate(-50%, -50%)', top: '50%', left: '50%' },
				'&-right': 	{ transform: 'translateY(-50%)', top: '50%', right: '0%' },
			},
			'&-bottom': {
				'&-left': 	{ bottom: '0%', left: '0%' },
				'&-center': { transform: 'translateX(-50%)', bottom: '0%', left: '50%' },
				'&-right': 	{ bottom: '0%', right: '0%' }
			}
		}
	}
};


// media queries
// http://giona.net/tools/css3-mediaquery-generator/
// standard embed sizes:
// 600x350 (in-content)
// 300x350 (rail standard)
// 300x250 (rail short)
const mediaQuery = {
	wide: `@media only screen and (min-width:${pxToEm(560)})`,
	medium: `@media only screen and (min-width:${pxToEm(400)}) and (max-width:${pxToEm(560)})`,
	thin: `@media only screen and (min-width:${pxToEm(0)}) and (max-width:${pxToEm(270)})`,
	tall: `@media only screen and (max-height:${pxToEm(450)})`,
	// short: `@media only screen and (max-height:${pxToEm(280)})`,
	short: `@media only screen and (min-height:${pxToEm(280)}) and (max-height:${pxToEm(340)})`,
	shorter: `@media only screen and (min-height:${pxToEm(250)}) and (max-height:${pxToEm(280)})`,
	shortest: `@media only screen and (min-height:${pxToEm(0)}) and (max-height:${pxToEm(250)})`,
};

const getElementCSSProperty = (element, property) => window.getComputedStyle(element, null).getPropertyValue(property);

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export { embedStyles, createStyles, convertHexToRGB, boxShadow, backgrounds, textShadow, pointsTotalDisplayText, fontSizeBaseline, pxToEm, pxToEmWithBaseline, pxToRem, percToEm, heights, spacing, animations, color, mediaQuery, imageTypes, button, displayTypes, embedCorners, fullSize, centerAbsolutely, replacePXinStringWithEM, convertStylesPXtoEM, getElementCSSProperty };
